<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 text-center">
          <div class="amej-title"><h1>Load Fund from Card</h1></div>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-6 col-md-8 col-sm-12">
          <div class="load-card">
            <h5 class="load-title">Add Funds to Wallet</h5>

            <!-- STEP 1 : amount -->
            <template v-if="step === 1">
              <div class="form-row-x">
                <label>Amount ({{ currency }})</label>
                <b-form-input
                  v-model="amount"
                  placeholder="0.00"
                  :class="errors.amount ? 'is-invalid' : ''"
                  oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                  @keyup.enter="startPayment"
                ></b-form-input>
                <span class="text-danger" v-if="errors.amount">{{ errors.amount }}</span>
              </div>

              <div class="text-danger mb-2" v-if="formError">{{ formError }}</div>

              <b-button class="w-100" variant="primary" :disabled="isStarting" @click="startPayment">
                <b-spinner small v-if="isStarting" class="mr-2"></b-spinner>
                {{ isStarting ? "Starting..." : "Continue to Payment" }}
              </b-button>
            </template>

            <!-- STEP 2 : Stripe card form -->
            <template v-else-if="step === 2">
              <div class="form-row-x">
                <label>Amount ({{ currency }})</label>
                <b-form-input :value="money(intent.amount || amount)" readonly class="readonly-amount"></b-form-input>
              </div>

              <div v-if="isMountingStripe" class="text-center py-4">
                <b-spinner></b-spinner>
                <p class="muted mt-2">Loading secure payment form...</p>
              </div>

              <!-- Stripe injects its own iframe here; card data never reaches our servers. -->
              <div ref="paymentElement" id="stripe-payment-element" v-show="!isMountingStripe"></div>

              <div class="text-danger mt-2" v-if="formError">{{ formError }}</div>
              <div class="success-text mt-2" v-if="successText">{{ successText }}</div>

              <b-button
                class="w-100 mt-3" variant="primary"
                :disabled="isPaying || isMountingStripe || !!successText"
                @click="confirmPayment"
              >
                <b-spinner small v-if="isPaying" class="mr-2"></b-spinner>
                {{ isPaying ? "Processing..." : "Add funds" }}
              </b-button>

              <div class="text-center mt-2">
                <a href="#" class="link-muted" v-if="!isPaying && !successText" @click.prevent="backToAmount">
                  Change amount
                </a>
              </div>

              <p class="secure-note">
                <span class="material-icons">lock</span>
                Payments are processed securely. Your card details are never stored on our servers.
              </p>
            </template>

            <!-- STEP 3 : done -->
            <template v-else>
              <div class="done-box">
                <span class="material-icons done-icon">check_circle</span>
                <p class="done-text">{{ successText }}</p>
                <p class="muted">Redirecting you to your dashboard...</p>
              </div>
              <b-button class="w-100" variant="primary" @click="goHome">Go to Dashboard</b-button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <b-toast id="load-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Payment Failed</strong>
      </div>
      <div><p>{{ formError }}</p></div>
    </b-toast>
  </div>
</template>

<script>
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";

const STRIPE_JS = "https://js.stripe.com/v3/";

export default {
  name: "LoadCards",
  components: { "app-header": AppHeader },
  data() {
    return {
      step: 1,
      amount: "",
      errors: {},
      formError: "",
      successText: "",
      isStarting: false,
      isPaying: false,
      isMountingStripe: false,
      intent: {},
      stripe: null,
      elements: null,
      paymentElement: null,
      redirectTimer: null,
    };
  },
  computed: {
    ...mapState(["profile", "paymentIntent"]),
    currency: function () {
      const a = this.profile && this.profile.author;
      return (a && a.default_currency) || "USD";
    },
  },
  beforeDestroy() {
    this.teardownStripe();
    if (this.redirectTimer) { clearTimeout(this.redirectTimer); }
  },
  methods: {
    ...mapActions(["initiatePayment", "listProfile"]),

    money(v) {
      const n = Number(v);
      return (isNaN(n) ? 0 : n).toFixed(2);
    },

    /** Stripe.js must come from their CDN - it cannot be bundled. Loaded once. */
    loadStripeJs() {
      if (window.Stripe) { return Promise.resolve(window.Stripe); }
      return new Promise(function (resolve, reject) {
        const existing = document.querySelector('script[src="' + STRIPE_JS + '"]');
        if (existing) {
          existing.addEventListener("load", function () { resolve(window.Stripe); });
          existing.addEventListener("error", function () { reject(new Error("stripe-js")); });
          return;
        }
        const s = document.createElement("script");
        s.src = STRIPE_JS;
        s.async = true;
        s.onload = function () { resolve(window.Stripe); };
        s.onerror = function () { reject(new Error("stripe-js")); };
        document.head.appendChild(s);
      });
    },

    validateAmount() {
      const e = {};
      const raw = String(this.amount).trim();
      const n = Number(raw);
      if (!raw) { e.amount = "Please enter an amount."; }
      else if (isNaN(n) || n <= 0) { e.amount = "Amount must be greater than 0."; }
      else if (n < 1) { e.amount = "The minimum load amount is 1.00."; }
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    /** STEP 1 -> POST payment/initiate, then mount Stripe with the returned keys. */
    startPayment() {
      this.formError = "";
      if (!this.validateAmount()) { return; }
      this.isStarting = true;
      const vm = this;
      const amount = Number(this.amount);
      return this.initiatePayment({
        gateway: "stripe",
        amount: amount,
        purpose: "wallet_load",
        remarks: "Load Funds from Card",
      }).then(function (res) {
        if (!res || !res.ok) {
          vm.formError = (res && res.message) || "Unable to start the payment.";
          vm.$bvToast.show("load-error-toast");
          return;
        }
        vm.intent = res.intent;
        vm.step = 2;
        return vm.$nextTick().then(function () { return vm.mountStripe(); });
      }).then(function () { vm.isStarting = false; })
        .catch(function () {
          vm.isStarting = false;
          vm.formError = "Unable to start the payment. Please try again.";
        });
    },

    /** Mounts Stripe's Payment Element against the order's client_secret. */
    mountStripe() {
      this.isMountingStripe = true;
      const vm = this;
      return this.loadStripeJs().then(function (Stripe) {
        if (!Stripe) { throw new Error("stripe-js"); }
        vm.teardownStripe();
        vm.stripe = Stripe(vm.intent.publishable_key);
        vm.elements = vm.stripe.elements({
          clientSecret: vm.intent.client_secret,
          appearance: { theme: "stripe", variables: { colorPrimary: "#0066b3" } },
        });
        vm.paymentElement = vm.elements.create("payment", { layout: "tabs" });
        vm.paymentElement.mount(vm.$refs.paymentElement);
        vm.paymentElement.on("ready", function () { vm.isMountingStripe = false; });
        vm.paymentElement.on("change", function (ev) {
          vm.formError = (ev && ev.error && ev.error.message) ? ev.error.message : "";
        });
        // Safety net: if `ready` never fires, still reveal the form.
        setTimeout(function () { vm.isMountingStripe = false; }, 4000);
      }).catch(function () {
        vm.isMountingStripe = false;
        vm.formError = "Could not load the secure payment form. Please check your connection and try again.";
        vm.$bvToast.show("load-error-toast");
      });
    },

    teardownStripe() {
      try {
        if (this.paymentElement && this.paymentElement.destroy) { this.paymentElement.destroy(); }
      } catch (e) { /* already gone */ }
      this.paymentElement = null;
      this.elements = null;
      this.stripe = null;
    },

    /** STEP 2 -> confirm with Stripe. The wallet is credited by the backend on success. */
    confirmPayment() {
      if (!this.stripe || !this.elements) {
        this.formError = "The payment form is not ready yet.";
        return;
      }
      this.formError = "";
      this.isPaying = true;
      const vm = this;
      return this.stripe.confirmPayment({
        elements: this.elements,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.origin + "/app",
        },
      }).then(function (result) {
        if (result.error) {
          // Card declined, validation error, etc.
          vm.formError = result.error.message || "Your payment could not be completed.";
          vm.$bvToast.show("load-error-toast");
          vm.isPaying = false;
          return;
        }
        const pi = result.paymentIntent || {};
        if (pi.status === "succeeded") { return vm.onPaid(); }
        if (pi.status === "processing") {
          vm.successText = "Payment received. Your wallet will be credited shortly.";
          return vm.finish();
        }
        if (pi.status === "requires_payment_method") {
          vm.formError = "That payment was not completed. Please try another card.";
          vm.$bvToast.show("load-error-toast");
          vm.isPaying = false;
          return;
        }
        vm.formError = "Payment status: " + (pi.status || "unknown") + ". Please try again.";
        vm.$bvToast.show("load-error-toast");
        vm.isPaying = false;
      }).catch(function () {
        vm.isPaying = false;
        vm.formError = "Your payment could not be completed. Please try again.";
        vm.$bvToast.show("load-error-toast");
      });
    },

    onPaid() {
      this.successText = "Payment successful! Crediting your wallet...";
      return this.finish();
    },

    finish() {
      const vm = this;
      this.isPaying = false;
      this.teardownStripe();
      // Refresh the balance, then move to the dashboard.
      return this.listProfile().catch(function () { /* balance refresh is best-effort */ })
        .then(function () {
          vm.step = 3;
          vm.redirectTimer = setTimeout(function () { vm.goHome(); }, 2500);
        });
    },

    backToAmount() {
      this.teardownStripe();
      this.formError = "";
      this.intent = {};
      this.step = 1;
    },

    goHome() {
      if (this.redirectTimer) { clearTimeout(this.redirectTimer); }
      this.$router.replace({ name: "app" }).catch(function () {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../css/style.scss';

.load-card {
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 12px;
  padding: 28px; margin-bottom: 40px;
}
.load-title { font-size: 18px; font-weight: 700; margin-bottom: 18px; }
.form-row-x { margin-bottom: 16px; }
.form-row-x label { display: block; font-size: 13px; color: #6c757d; margin-bottom: 6px; }
.readonly-amount { background: #f1f3f5; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.success-text { color: #00a800; font-size: 13px; font-weight: 600; }
.muted { color: #6c757d; font-size: 13px; }
.link-muted { color: #0066b3; font-weight: 600; font-size: 13px; }
.w-100 { width: 100%; }
.secure-note {
  display: flex; align-items: center; gap: 6px; justify-content: center;
  font-size: 12px; color: #6c757d; margin-top: 14px; margin-bottom: 0;
}
.secure-note .material-icons { font-size: 15px; }
.done-box { text-align: center; padding: 18px 0 8px; }
.done-icon { font-size: 54px; color: #00a800; }
.done-text { font-size: 16px; font-weight: 600; color: #00a800; margin: 10px 0 4px; }
#stripe-payment-element { margin-top: 6px; }
</style>
